import axios from 'axios';
import { getConfig } from '../config';
import { AdminApiDomain, getAdminApiUrl } from '../config/ApiRouting';
import type {
  SkinSeoulAdminProduct,
  PublicationStatus,
  SkinSeoulProductType,
  StockStatus,
  CpProductStatus,
} from '../models/ProductTypes';

interface ProductsResponse {
  products: SkinSeoulAdminProduct[];
  total: number;
  page: number;
  limit: number;
}

export interface CreditBoostUpdateItem {
  sku: string;
  creditBoostPercent: number;
  creditBoostOverride?: boolean;
}

interface CreditBoostSimpleUpdate extends CreditBoostUpdateItem {
  variations?: never;
}

export interface CreditBoostVariationWrapper {
  sku: string;
  variations: CreditBoostUpdateItem[];
}

export type CreditBoostUpdateProduct = CreditBoostSimpleUpdate | CreditBoostVariationWrapper;
export type CreditBoostUpdateRequestProduct = CreditBoostUpdateProduct;

export type CreditBoostUpdateResult =
  | {
      sku: string;
      success: true;
      productId?: number;
      creditBoostPercent: number;
      creditBoostRevision: string;
      warning?: 'above_recommended_limit';
    }
  | {
      sku: string;
      success: false;
      productId?: number;
      error: string;
    };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const isOptionalProductId = (value: unknown): value is number | undefined =>
  value === undefined || (Number.isSafeInteger(value) && Number(value) > 0);

const readCreditBoostResult = (value: unknown, request: CreditBoostUpdateItem): CreditBoostUpdateResult | null => {
  if (!isRecord(value) || value['sku'] !== request.sku || !isOptionalProductId(value['productId'])) {
    return null;
  }

  const productId = value['productId'];
  if (value['success'] === false) {
    const error = value['error'];
    if (
      typeof error !== 'string' ||
      error.length === 0 ||
      value['creditBoostPercent'] !== undefined ||
      value['creditBoostRevision'] !== undefined ||
      value['warnings'] !== undefined
    ) {
      return null;
    }
    return { sku: request.sku, success: false, ...(productId === undefined ? {} : { productId }), error };
  }

  const revision = value['creditBoostRevision'];
  const expectsWarning = request.creditBoostPercent > 10;
  const warning = isRecord(value['warnings']) ? value['warnings']['creditBoost'] : undefined;
  if (
    value['success'] !== true ||
    value['error'] !== undefined ||
    value['creditBoostPercent'] !== request.creditBoostPercent ||
    typeof revision !== 'string' ||
    !/^(0|[1-9]\d*)$/.test(revision) ||
    (expectsWarning ? warning !== 'above_recommended_limit' : value['warnings'] !== undefined)
  ) {
    return null;
  }
  return {
    sku: request.sku,
    success: true,
    ...(productId === undefined ? {} : { productId }),
    creditBoostPercent: request.creditBoostPercent,
    creditBoostRevision: revision,
    ...(expectsWarning ? { warning: 'above_recommended_limit' as const } : {}),
  };
};

const readCreditBoostResponse = (
  value: unknown,
  request: CreditBoostUpdateRequestProduct[],
): CreditBoostUpdateResult[] | null => {
  if (!isRecord(value) || !Array.isArray(value['products']) || value['products'].length !== request.length) {
    return null;
  }
  const results: CreditBoostUpdateResult[] = [];
  for (let index = 0; index < request.length; index += 1) {
    const requestedProduct = request[index];
    const productResult = value['products'][index];
    if (!requestedProduct || !isRecord(productResult) || productResult['sku'] !== requestedProduct.sku) {
      return null;
    }

    if ('variations' in requestedProduct) {
      const variationResults = productResult['variations'];
      if (
        productResult['success'] !== true ||
        productResult['error'] !== undefined ||
        productResult['creditBoostPercent'] !== undefined ||
        productResult['creditBoostRevision'] !== undefined ||
        !Array.isArray(variationResults) ||
        variationResults.length !== requestedProduct.variations.length
      ) {
        return null;
      }
      for (let variationIndex = 0; variationIndex < requestedProduct.variations.length; variationIndex += 1) {
        const parsed = readCreditBoostResult(
          variationResults[variationIndex],
          requestedProduct.variations[variationIndex]!,
        );
        if (!parsed) {
          return null;
        }
        results.push(parsed);
      }
      continue;
    }

    const parsed = readCreditBoostResult(productResult, requestedProduct);
    if (!parsed || productResult['variations'] !== undefined) {
      return null;
    }
    results.push(parsed);
  }
  return results;
};

export interface CategoryTerm {
  termId: number;
  name: string;
  slug: string;
  taxonomy: string;
  parentTermId?: number;
  thumbnailImageUrl?: string;
}

export interface BrandTerm {
  termId: number;
  name: string;
  slug: string;
  taxonomy: string;
  thumbnailImageUrl?: string;
}

/** The fields of a shop product (Facade byIds) that by-id readers use; the shop strips the admin-only ones. */
export interface PublishedProductSummary {
  productId?: number;
  sku: string;
  name: string;
  productImageUrls?: string[];
}

export class SkinSeoulProductsClient {
  // admin paths (/products/admin, catalog/all) → internal-api; /:id + taxonomy → facade (not yet migrated to internal).
  private readonly adminBaseUrl: string;
  private readonly facadeBaseUrl: string;
  private readonly apiKey: string;

  constructor() {
    this.adminBaseUrl = getAdminApiUrl(AdminApiDomain.PRODUCT);
    this.facadeBaseUrl = getConfig().apiUrl;
    this.apiKey = getConfig().apiKey;
  }

  async getAllProducts(
    page = 1,
    limit = 20,
    postStatus?: PublicationStatus,
    productType?: SkinSeoulProductType,
    stockStatus?: StockStatus,
    wave?: string,
    brandIds?: string,
    categoryIds?: string,
    cpProductStatus?: CpProductStatus,
    cpLastScrapedAt?: string,
    name?: string,
    gpi?: string,
  ): Promise<ProductsResponse> {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());

    if (postStatus) {
      params.append('publicationStatus', postStatus);
    }

    if (productType) {
      params.append('productType', productType);
    }

    if (stockStatus) {
      params.append('stockStatus', stockStatus);
    }

    if (wave && wave !== 'all') {
      params.append('wave', wave);
    }

    if (brandIds) {
      params.append('brandIds', brandIds);
    }

    if (categoryIds) {
      params.append('categoryIds', categoryIds);
    }

    if (cpProductStatus) {
      params.append('cpProductStatus', cpProductStatus);
    }

    if (cpLastScrapedAt) {
      params.append('cpLastScrapedAt', cpLastScrapedAt);
    }

    if (name) {
      params.append('name', name);
    }

    if (gpi) {
      params.append('gpi', gpi);
    }

    const url = `${this.adminBaseUrl}/products/catalog/all?${params.toString()}`;
    console.info('🌐 API Request:', url);

    const response = await axios.get<ProductsResponse>(url, {
      headers: { 'X-API-Key': this.apiKey },
    });
    return response.data;
  }

  async searchProductsByName(name: string, limit = 10): Promise<ProductsResponse> {
    return this.getAllProducts(
      1,
      limit,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      name,
    );
  }

  /**
   * The shop's own product search (Meilisearch behind `/products/search`): fuzzy over name, brand and
   * description, Korean queries included, ranked the way skin-seoul.com ranks them. Used where an operator
   * names a product they are holding, so the list reads like the shop and not like a database filter.
   */
  async searchStorefront(query: string, limit = 8): Promise<ProductsResponse> {
    const params = new URLSearchParams();
    params.append('q', query);
    params.append('limit', limit.toString());
    const url = `${this.facadeBaseUrl}/products/search?${params.toString()}`;
    console.info('🌐 API Request (Storefront Search):', url);

    // The shop search is public; the admin key stays out of a request that never needed it.
    const response = await axios.get<ProductsResponse>(url);
    return response.data;
  }

  /**
   * One published product by id. The Facade has no /products/:id route (it answers 404 for every id), so this reads
   * the shop's byIds list; that list holds published products only, so a draft comes back as not found.
   */
  async getProductById(productId: string): Promise<PublishedProductSummary> {
    const url = `${this.facadeBaseUrl}/products/byIds?ids=${encodeURIComponent(productId)}`;
    console.info('🌐 API Request:', url);

    const response = await axios.get<PublishedProductSummary[]>(url);
    const product = response.data?.[0];
    if (!product) {
      throw new Error(`Product ${productId} not found`);
    }
    return product;
  }

  async getProductsBySku(skus: string[], publicationStatus?: PublicationStatus): Promise<SkinSeoulAdminProduct[]> {
    const params = new URLSearchParams();
    params.append('skus', skus.join(','));

    if (publicationStatus) {
      params.append('publicationStatus', publicationStatus);
    }

    const url = `${this.adminBaseUrl}/products/admin?${params.toString()}`;
    console.info('🌐 API Request:', url);

    const response = await axios.get<SkinSeoulAdminProduct[]>(url, {
      headers: { 'X-API-Key': this.apiKey },
    });
    return response.data;
  }

  /**
   * `current: true` bypasses the shared product cache (7-day TTL) and reads current metadata. Use it
   * only for loading a single product into an editor, not for lists or bulk lookups.
   */
  async searchProductsBySku(
    sku: string,
    postStatus?: PublicationStatus,
    options: { current?: boolean } = {},
  ): Promise<SkinSeoulAdminProduct[]> {
    const params = new URLSearchParams();
    params.append('skus', sku);

    if (postStatus) {
      params.append('publicationStatus', postStatus);
    }

    if (options.current) {
      params.append('current', 'true');
    }

    const url = `${this.adminBaseUrl}/products/admin?${params.toString()}`;
    console.info('🌐 API Request (SKU Search):', url);

    const response = await axios.get<SkinSeoulAdminProduct[]>(url, {
      headers: { 'X-API-Key': this.apiKey },
    });
    return response.data;
  }

  async getAllCategories(): Promise<CategoryTerm[]> {
    const url = `${this.facadeBaseUrl}/product/taxonomy/categories/all`;
    console.info('🌐 API Request (Categories):', url);

    const response = await axios.get<CategoryTerm[]>(url);
    return response.data;
  }

  async getAllBrands(): Promise<BrandTerm[]> {
    const url = `${this.facadeBaseUrl}/product/taxonomy/brands`;
    console.info('🌐 API Request (Brands):', url);

    const response = await axios.get<BrandTerm[]>(url);
    return response.data;
  }

  async updateProducts(products: any[]): Promise<{ success: boolean; message?: string }> {
    const url = `${this.adminBaseUrl}/products/admin`;
    console.info('🔄 Updating products:', products);

    try {
      const response = await axios.patch(
        url,
        { products },
        {
          headers: {
            'Content-Type': 'application/json',
            'X-API-Key': this.apiKey,
          },
        },
      );

      console.info('✅ Products updated successfully');
      return {
        success: true,
        message: response.data?.message || 'Products updated successfully',
      };
    } catch (error) {
      console.error('❌ Failed to update products:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  async updateCreditBoosts(products: CreditBoostUpdateRequestProduct[]): Promise<CreditBoostUpdateResult[]> {
    const response = await axios
      .patch<unknown>(
        `${this.adminBaseUrl}/products/admin`,
        { products },
        {
          headers: {
            'X-API-Key': this.apiKey,
            'Content-Type': 'application/json',
          },
        },
      )
      .catch(() => {
        throw new Error('Could not confirm credit boost update');
      });
    const result = response.status === 200 ? readCreditBoostResponse(response.data, products) : null;
    if (!result) {
      throw new Error('Credit boost response is invalid');
    }
    return result;
  }
}

export const skinSeoulProductsClient = new SkinSeoulProductsClient();
